import { Collapse, Group, Stack, Text } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { Badge, BadgeVariant, ClickableText, ToggleCollapse } from "@tfg/ui"
import { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  CertificateByOrganizationDto,
  FindCertificateRequestsByCertificateIdItem,
  FindCertificateRequestsByCertificateIdItemGroup,
} from "~/gen/api"
import { CertificateRequestActions } from "~/pages/Brand/Certificates/components/CertificateRequestActions"
import { CertificateRequestValidation } from "~/pages/Brand/Certificates/components/CertificateRequestValidation"

type SubmittedCertificateRequestsProps = {
  data: FindCertificateRequestsByCertificateIdItemGroup
  certificate: CertificateByOrganizationDto
}

type BatchSelectAction = {
  text: string
  onClick: () => void
}

export const SubmittedCertificateRequests: React.FC<
  SubmittedCertificateRequestsProps
> = ({ data, certificate }: SubmittedCertificateRequestsProps) => {
  const { t } = useTranslation(["certificates", "common"])

  const [checkedCertificateRequests, setCheckedCertificateRequests] = useState<
    string[]
  >([])

  const [opened, { toggle: toggle }] = useDisclosure(true)

  const seeMoreLabelComponent = (
    <Group gap="xs">
      <Text fw={600} size="md" style={{ fontWeight: 600 }}>
        {t("certificates:certificateRequestStatus.SUBMITTED")}
      </Text>
      <Badge
        variant={BadgeVariant.SECONDARY}
        text={data.count.toString()}
        size="xs"
      />
    </Group>
  )

  const allChecked = useMemo(
    () => checkedCertificateRequests.length === data.count,
    [checkedCertificateRequests.length, data.count]
  )

  const handleBatchSelectAction = useCallback((): BatchSelectAction => {
    if (allChecked) {
      return {
        text: t("common:actionButtons.deselectAll"),
        onClick: () => {
          setCheckedCertificateRequests([])
        },
      }
    } else {
      return {
        text: t("common:actionButtons.selectAll"),
        onClick: () => {
          setCheckedCertificateRequests(
            data.certificateRequests.map((request) => request.id)
          )
        },
      }
    }
  }, [allChecked, data.certificateRequests, t])

  if (!data.certificateRequests || data.count === 0) {
    return null
  }

  return (
    <>
      <Group justify="space-between">
        <ToggleCollapse
          open={opened}
          toggle={toggle}
          seeMoreLabel={seeMoreLabelComponent}
          seeLessLabel={seeMoreLabelComponent}
        />
        {opened ? <ClickableText {...handleBatchSelectAction()} /> : null}
      </Group>
      <Collapse in={opened}>
        {data.certificateRequests.map(
          (certificateRequest: FindCertificateRequestsByCertificateIdItem) => (
            <CertificateRequestValidation
              key={certificateRequest.id}
              certificateRequest={certificateRequest}
              certificate={certificate}
              checked={checkedCertificateRequests.includes(
                certificateRequest.id
              )}
              onCheckboxChange={(checked) => {
                setCheckedCertificateRequests((prev) =>
                  checked
                    ? Array.from(new Set([...prev, certificateRequest.id]))
                    : Array.from(
                        new Set(
                          prev.filter((id) => id !== certificateRequest.id)
                        )
                      )
                )
              }}
            />
          )
        )}
        <Stack pos="sticky" bottom={0} mah="9vh" p="sm" bg="white" mt="lg">
          <CertificateRequestActions
            certificateRequestIds={checkedCertificateRequests}
            certificate={certificate}
            onActionSelected={() => {
              setCheckedCertificateRequests([])
            }}
          />
        </Stack>
      </Collapse>
    </>
  )
}
